import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/packages', label: 'Packages' },
  { path: '/gallery', label: 'Gallery' },
  { path: '/services', label: 'Services' },
  { path: '/design-your-theater', label: 'Room Designer' },
  { path: '/contact', label: 'Contact' },
  { path: '/book', label: 'Book Now' },
  { path: '/portal', label: 'Client Login' },
];

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0f0f10]/90 border-b border-white/5" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img src="/brand/logo-full.png" alt="DreamRoom Theaters" className="h-16 lg:h-20 w-auto object-contain py-1" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map(link => {
              const isActive = location.pathname === link.path;
              const isBookNow = link.path === '/book';
              const isPortal = link.path === '/portal';
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    px-4 py-2 rounded-sm font-manrope text-xs uppercase tracking-[0.15em] transition-all duration-300
                    ${isBookNow
                      ? 'bg-[#D4AF37] text-black font-bold hover:bg-[#C5A059] ml-2'
                      : isPortal
                        ? 'border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 ml-2'
                        : isActive
                          ? 'text-[#D4AF37]'
                          : 'text-white/50 hover:text-white'
                    }
                  `}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white/60 hover:text-[#D4AF37] transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-white/5 bg-[#0f0f10]/98 backdrop-blur-xl overflow-hidden"
            data-testid="mobile-nav"
          >
            <nav className="px-6 py-4 space-y-1">
              {navLinks.map(link => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={`
                      block px-4 py-3 rounded-sm font-manrope text-sm uppercase tracking-widest transition-all duration-300
                      ${isActive ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-white/50 hover:text-white hover:bg-white/5'}
                    `}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
